'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useLanguage } from '@/context/LanguageContext';
import { getTranslations, SUPPORTED_LANGUAGES } from '@/lib/i18n';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();
  const { language, setLanguage } = useLanguage();
  const t = getTranslations(language);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (href: string) => pathname === href;

  const navLinks = [
  { label: t.nav.about, href: '/about' },
  { label: t.nav.programs, href: '/programs' },
  { label: t.nav.impact, href: '/impact' },
  { label: t.nav.events, href: '/events' },
  { label: t.nav.news, href: '/news' },
  { label: t.nav.contact, href: '/contact' }];


  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled ?
      'bg-white/95 backdrop-blur-xl shadow-md border-b border-neutral-100' :
      'bg-white/80 backdrop-blur-md'}`
      }>

      {/* Top bar */}
      <div className="bg-indigo text-white text-xs py-1.5 px-4 text-center hidden sm:block">
        <span className={language === 'kn' ? 'font-kannada' : ''}>{t.nav.topBar}</span>
        <span className="mx-3 opacity-40">|</span>
        <span>{t.nav.location}</span>
        <span className="mx-3 opacity-40">|</span>
        <a href="tel:+919876543210" className="hover:text-saffron-light transition-colors">+91 7353101553</a>
      </div>

      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/home" className="flex items-center gap-3 group">
            <Image
              src="/assets/images/make_a_3d_model_of_202605151251-removebg-preview-1778920681336.png"
              alt="Arivu Foundation Logo"
              width={80}
              height={80}
              className="object-contain" />

            <div className="flex flex-col">
              <span className="font-bold text-lg text-indigo leading-tight tracking-tight">Arivu Foundation</span>
              <span className="text-xs text-neutral-500 leading-tight font-kannada">ಅರಿವು ಫೌಂಡೇಶನ್</span>
            </div>
          </Link>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((l) =>
            <Link
              key={l.href}
              href={l.href}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              isActive(l.href) ?
              'text-saffron bg-saffron-pale' : 'text-neutral-700 hover:text-saffron hover:bg-saffron-pale'} ${
              language === 'kn' ? 'font-kannada' : ''}`}>

                {l.label}
              </Link>
            )}
          </div>

          {/* CTAs + Language Toggle */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Language Toggle */}
            <div className="flex items-center rounded-full border border-neutral-200 overflow-hidden bg-neutral-50">
              {SUPPORTED_LANGUAGES.map((lang) =>
              <button
                key={lang.code}
                onClick={() => setLanguage(lang.code)}
                className={`px-3 py-1.5 text-xs font-semibold transition-all duration-200 ${
                language === lang.code ?
                'bg-indigo text-white' : 'text-neutral-600 hover:bg-neutral-100'} ${
                lang.code === 'kn' ? 'font-kannada' : ''}`}
                aria-label={`Switch to ${lang.label}`}>

                  {lang.nativeLabel}
                </button>
              )}
            </div>
            <Link
              href="/get-involved"
              className={`px-4 py-2 text-sm font-semibold text-indigo border-2 border-indigo rounded-full hover:bg-indigo hover:text-white transition-all duration-200 ${language === 'kn' ? 'font-kannada' : ''}`}>

              {t.nav.volunteer}
            </Link>
            <Link
              href="/donate"
              className={`px-5 py-2 text-sm font-semibold text-white rounded-full btn-saffron ${language === 'kn' ? 'font-kannada' : ''}`}>

              {t.nav.donateNow}
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden w-10 h-10 rounded-lg flex items-center justify-center text-neutral-700 hover:bg-neutral-100 transition-colors"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu">

            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              {menuOpen ?
              <path d="M4 4L18 18M18 4L4 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /> :

              <path d="M3 6h16M3 11h16M3 16h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              }
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {menuOpen &&
      <div className="lg:hidden bg-white border-t border-neutral-100 shadow-xl">
          <div className="px-4 py-4 flex flex-col gap-1">
            {/* Mobile Language Toggle */}
            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-neutral-100">
              <span className="text-xs text-neutral-500 font-medium">Language:</span>
              <div className="flex items-center rounded-full border border-neutral-200 overflow-hidden bg-neutral-50">
                {SUPPORTED_LANGUAGES.map((lang) =>
              <button
                key={lang.code}
                onClick={() => setLanguage(lang.code)}
                className={`px-3 py-1.5 text-xs font-semibold transition-all duration-200 ${
                language === lang.code ?
                'bg-indigo text-white' : 'text-neutral-600 hover:bg-neutral-100'} ${
                lang.code === 'kn' ? 'font-kannada' : ''}`}>

                    {lang.nativeLabel}
                  </button>
              )}
              </div>
            </div>
            {navLinks.map((l) =>
          <Link
            key={l.href}
            href={l.href}
            onClick={() => setMenuOpen(false)}
            className={`px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
            isActive(l.href) ?
            'text-saffron bg-saffron-pale' : 'text-neutral-700 hover:text-saffron hover:bg-saffron-pale'} ${
            language === 'kn' ? 'font-kannada' : ''}`}>

                {l.label}
              </Link>
          )}
            <div className="flex gap-3 mt-3 pt-3 border-t border-neutral-100">
              <Link
              href="/get-involved"
              onClick={() => setMenuOpen(false)}
              className={`flex-1 py-2.5 text-sm font-semibold text-center text-indigo border-2 border-indigo rounded-full hover:bg-indigo hover:text-white transition-all ${language === 'kn' ? 'font-kannada' : ''}`}>

                {t.nav.volunteer}
              </Link>
              <Link
              href="/donate"
              onClick={() => setMenuOpen(false)}
              className={`flex-1 py-2.5 text-sm font-semibold text-center text-white rounded-full btn-saffron ${language === 'kn' ? 'font-kannada' : ''}`}>

                {t.nav.donateNow}
              </Link>
            </div>
          </div>
        </div>
      }
    </header>);

}